import { combineReducers } from "redux";
import { Constants } from "./constants";
import { Lineup, LineupRubric } from "./types";
import { Projection } from "../../common/EasyProjection/types";
import { showdownMultipiers } from "../../common/Table/LineupBuilderConfig";
import { canAddPlayerToSlot } from "../helpers/specialPositionsHelper";

const clearLineupPlayer = (slot: LineupRubric): LineupRubric => ({
  ...slot,
  name: '',
  team: '',
  teamLogo: '',
  opponent: '',
  opponentLogo: '',
  fpp: 0,
  ownership: 0,
  proOwnership: 0,
  starOwnership: 0,
  cptOwnership: 0,
  salary: 0,
  nameAndId: '',
  confirmedLineup: false,
  canBeAddedToPositions: [],
  isLocked: false,
});

const findAndReplacePlayers = (positions: LineupRubric[], player: Projection, site?: string) => {
  let placedPlayer = false;
  const openPosition = positions.find(x => x.position === player.position && x.name === '');
  const newPositionList = positions.map(x => {
    // Removing an existing player must not depend on which eligible slots are empty.
    if (player.name && x.name === player.name) {
      placedPlayer = true;
      return clearLineupPlayer(x);
    } else if(openPosition !== undefined && openPosition?.position !== x.position) {
      return x
    } else if(!placedPlayer
      && canAddPlayerToSlot(player, x.position)
      && x.name === ''
      && !positions.find(x => x.name === player.name)) {
      placedPlayer = true;
      // Check if this specific player was previously locked (by nameAndId)
      const wasPlayerLocked = positions.find(p => p.nameAndId === player.nameAndId)?.isLocked || false;
      return {
        ...x,
        name: player.name,
        team: player.team,
        teamLogo: player.teamLogo,
        isLocked: wasPlayerLocked,
        opponent: player.opponent,
        opponentLogo: player.opponentLogo,
        fpp: player.projection,
        ownership: player.ownership,
        proOwnership: player.proOwnership,
        starOwnership: player.starOwnership,
        cptOwnership: player.cptOwnership,
        salary: player.salary,
        nameAndId: player.nameAndId,
        gameTime: player.gameTime,
        confirmedLineup: player.confirmedLineup,
        lineupPosition: player.lineupPosition,
        canBeAddedToPositions: player.canBeAddedToPositions,
        ...showdownMultipiers(player, x.position)
      } as LineupRubric
    }
    return x;
  })

  return newPositionList;
}

const updatePlayerForShowdown = (player: Projection) => {
  return {
    ...player,
    ...showdownMultipiers(player, player.position)
  }
}

const refreshLineupEligibility = (lineups: Lineup[], projections: Projection[]) => {
  const byId = new Map(projections.map(player => [player.nameAndId, player]));
  return lineups.map(lineup => ({
    ...lineup,
    players: lineup.players.map(player => {
      const projection = byId.get(player.nameAndId);
      return projection ? { ...player, canBeAddedToPositions: projection.canBeAddedToPositions } : player;
    }),
  }));
};

export const lineups = (lineups = [] as Lineup[], action: any): Lineup[] => {
  switch (action.type) {
    case Constants.CREATE_LINEUP:
      return [...lineups, action.payload]
    case Constants.SET_LINEUP:
      return lineups
      .map(x => {
        if (x.id === action.payload.lineupId) {
          return {
            ...x, 
            players: action.payload.players.map((y) => {
              // Find if this player was previously locked by matching nameAndId
              const previousPlayer = x.players.find(p => p.nameAndId === y.nameAndId);
              const isLocked = previousPlayer?.isLocked || false;
              return updatePlayerForShowdown({
                ...y, 
                fpp: y.projection, 
                position: x.players.find(z => z.position === y.position)?.position,
                isLocked: isLocked
              });
            })
          };
        }
        return x;
      });
    case Constants.ADD_PLAYER:
      return lineups
      .map(x => {
        return x.id === action.payload.lineupId ? {...x, players: findAndReplacePlayers(x.players, action.payload.player, action.payload.site)} : x
      });
    case Constants.REMOVE_PLAYER:
      return lineups.map(x => {
        const player = action.payload.player;
        return x.id === action.payload.lineupId ? {...x, players: x.players.map(slot => {
          const matchesPlayer = player.nameAndId
            ? slot.nameAndId === player.nameAndId
            : player.name && slot.name === player.name;
          return matchesPlayer ? clearLineupPlayer(slot) : slot;
        }), simResults: [], simStatus: 'incomplete', lineupChecked: false, lineupErrors: []} : x
      });
    case Constants.SET_LINEUP_CHECKED:
      return lineups.map(x => {
        return x.id === action.payload.lineupId ? {...x, lineupChecked: action.payload.isValid, lineupErrors: action.payload.errorMessage ? [action.payload.errorMessage] : []} : x
      });
    case Constants.SET_SIM_RESULTS:
      return lineups
      .map(x => {
        return x.id === action.payload.lineupId ? {...x, simStatus: action.payload.simStatus, simResults: action.payload.simResults} : x
      });
    case Constants.SET_SAVED_LINEUPS:
      return refreshLineupEligibility(action.payload, action.projections ?? []);
    case Constants.SET_SLATE_PROJECTIONS:
      return refreshLineupEligibility(lineups, action.payload);
    case Constants.RESET_LINEUP:
      return lineups.map(x => {
        if (x.id === action.payload.lineupId) {
          return {
            ...x,
            fpp: x.players.filter(p => p.isLocked).reduce((sum, p) => sum + p.fpp, 0),
            ownership: x.players.filter(p => p.isLocked).reduce((sum, p) => sum + p.ownership, 0),
            players: x.players.map(player => {
              // If player is locked, keep them as is
              if (player.isLocked) {
                return player;
              }
              // Otherwise, clear the player
              return {
                ...player,
                name: '',
                team: '',
                opponent: '',
                teamLogo: '',
                opponentLogo: '',
                fpp: 0,
                ownership: 0,
                proOwnership: 0,
                starOwnership: 0,
                cptOwnership: 0,
                salary: 0,
                nameAndId: '',
                gameTime: '',
              };
            }),
            simResults: [],
            simStatus: 'incomplete',
            lineupChecked: false,
            lineupErrors: [],
          }
        }
        return x
      });
    case Constants.DELETE_LINEUP:
      return lineups.filter(x => x.id !== action.payload);
    case Constants.DELETE_ALL_LINEUPS:
      return [];
    case Constants.TOGGLE_PLAYER_LOCK:
      return lineups.map(x => {
        if (x.id === action.payload.lineupId) {
          return {
            ...x,
            players: x.players.map(player => {
              if (player.nameAndId === action.payload.playerNameAndId && player.name !== '') {
                return { ...player, isLocked: !player.isLocked };
              }
              return player;
            })
          };
        }
        return x;
      });
    case Constants.SET_LINEUP_RANDOMNESS:
      return lineups.map(x => {
        if (x.id === action.payload.lineupId) {
          return { ...x, randomness: action.payload.randomness };
        }
        return x;
      });
    default:
      return lineups;
  }
}

export const selectedLineupId = (selectedLineupId = null, action: any) => {
  switch (action.type) {
    case Constants.SELECT_LINEUP:
      return action.payload;
    default:
      return selectedLineupId;
  }
}

export const lineupRubric = (lineupRubric = [] as LineupRubric[], action: any) => {
  switch (action.type) {
    case Constants.SET_LINEUP_RUBRIC:
      const lineupRubrics = action.payload.split(',').map((x: string, index: number) => ({
        position: x,
        gameTime: '',
        name:'',
        nameAndId: '',
        team: '',
        opponent: '',
        fpp: 0,
        ownership: 0,
        cptOwnership: 0,
        starOwnership: 0,
        proOwnership: 0,
        salary: 0,
        id: index + 1
      }));
      return lineupRubrics;
    default:
      return lineupRubric;
  }
}

export const selectedMatchups = (selectedMatchups = [], action: any) => {
  switch (action.type) {
    case Constants.SET_SELECTED_MATCHUPS:
      return action.payload;
    default:
      return selectedMatchups;
  }
}

export const csvHeaders = (csvHeaders = '', action: any) => {
  switch (action.type) {
    case Constants.SET_CSV_HEADERS:
      return action.payload;
    default:
      return csvHeaders;
  }
}

export const poolSize = (poolSize = 250, action: any) => {
  switch (action.type) {
    case Constants.SET_POOL_SIZE:
      return action.payload;
    default:
      return poolSize;
  }
}

export const percentToFirst = (percentToFirst = 20, action: any) => {
  switch (action.type) {
    case Constants.SET_PERCENT_TO_FIRST:
      return action.payload;
    default:
      return percentToFirst;
  }

}

export const projections = (projections = [] as Projection[], action: any) => {
  switch (action.type) {
    case Constants.SET_PROJECTIONS:
      return action.payload;
    default:
      return projections;
  }

}

export const slateProjections = (slateProjections = [] as Projection[], action: any) => {
  switch (action.type) {
    case Constants.SET_SLATE_PROJECTIONS:
      return action.payload;
    default:
      return slateProjections;
  }
}

export const defaultRandomness = (defaultRandomness = 0, action: any) => {
  switch (action.type) {
    case Constants.SET_DEFAULT_RANDOMNESS:
      return action.payload;
    default:
      return defaultRandomness;
  }
}

export const lineupBuilderReducer = combineReducers({
  lineups,
  selectedLineupId,
  lineupRubric,
  selectedMatchups,
  csvHeaders,
  poolSize,
  percentToFirst,
  projections,
  slateProjections,
  defaultRandomness
});
